import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import PrioritiesPicker from '../../../../../common/ui/priority-picker/main.tsx';
import { PriorityIconContainer } from '../../../../../common/ui/styled';
import type { Page, Priority, PriorityOption } from '../../../../../common/ui/types';
import { PRIORITY_PICKER_PAGE_SIZE } from '../../../../../constants';
import messages from './messages';

export type MappingPickerProps = {
	/**
	 * The priority that is currently selected by this dropdown menu.
	 * If null no priority has been selected.
	 */
	selectedPriority: Priority | null;
	/**
	 * Represents the priorities that can be selected or mapped to. This is used to
	 * render the dropdown menu options.
	 */
	mappablePriorities: Priority[];
	/**
	 * Callback to update mapping to priority param.
	 * @param priority what priority is being mapped to.
	 */
	setSelectedPriority: (priority: Priority) => void;
	/**
	 * Making this a prop to for storybooks. By default, this will be set to 10.
	 */
	pickerPageSize?: number;
	/**
	 * Can be used to set initial state of dropdown, used primarily for storybook testing.
	 * By default, the dropdown will be closed.
	 */
	defaultOpen?: boolean;
};

export const MappingPicker = ({
	selectedPriority,
	mappablePriorities,
	setSelectedPriority,
	pickerPageSize = PRIORITY_PICKER_PAGE_SIZE,
	defaultOpen = false,
}: MappingPickerProps) => {
	const { formatMessage } = useIntl();
	const mappablePriorityWithSelectedPriorityFiltered = mappablePriorities.filter(
		(priority: Priority) => priority.id !== selectedPriority?.id,
	);

	const intialPage: Page<Priority> = {
		items: mappablePriorityWithSelectedPriorityFiltered.slice(0, pickerPageSize),
		currentPage: 1,
		pageSize: pickerPageSize,
		totalItems: mappablePriorityWithSelectedPriorityFiltered.length,
	};

	const [page, setPage] = useState<Page<Priority>>(intialPage);

	const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
	const [pickerFilter, setPickerFilter] = useState('');

	const onOpen = () => {
		setIsOpen(true);
		setPage(intialPage);
		setPickerFilter('');
	};

	const onClose = () => {
		setIsOpen(false);
		setPage(intialPage);
		setPickerFilter('');
	};

	const onLoadMore = () => {
		const { currentPage } = page;

		setPage({
			...page,
			items: mappablePriorityWithSelectedPriorityFiltered.slice(
				0,
				(currentPage + 1) * pickerPageSize,
			),
			currentPage: currentPage + 1,
			pageSize: pickerPageSize,
			totalItems: mappablePriorityWithSelectedPriorityFiltered.length,
		});
	};

	const onFilterChange = (value: string) => {
		setPickerFilter(value);
		const filteredList = mappablePriorityWithSelectedPriorityFiltered.filter((priority) =>
			priority.name.toLowerCase().includes(value.toLowerCase()),
		);
		setPage({
			items: filteredList.slice(0, pickerPageSize),
			currentPage: 1,
			pageSize: pickerPageSize,
			totalItems: filteredList.length,
		});
	};

	const onSelect = (priorityOption: PriorityOption) => {
		/**
		 * Reconstructing priority here to deal with priorityOption type which is
		 * used by the picker -> consider make it just priority
		 *
		 * If not, things to consider, if we care about the default priority being
		 * selected in the future we can reconstruct it from mappablePriorities list.
		 */
		setSelectedPriority({
			...priorityOption,
			isDefault: false,
		});

		setIsOpen(false);
	};

	return (
		<DropdownMenuWrapper>
			<PrioritiesPicker
				showCheckboxes={false}
				selectedPriorityIds={null}
				isOpen={isOpen}
				renderTrigger={({ triggerRef, ...props }) => (
					<Button
						{...props}
						onClick={() => (isOpen ? onClose() : onOpen())}
						ref={triggerRef}
						shouldFitContainer
					>
						{selectedPriority ? (
							<MappingDropdownContainer>
								<PriorityIconContainer src={selectedPriority.iconUrl} alt={selectedPriority.name} />
								{selectedPriority.name}
								<ChevronDownIcon
									label={formatMessage(messages.mappingModalDropdownChevronDownIconLabel)}
								/>
							</MappingDropdownContainer>
						) : (
							<MappingDropdownContainer>
								{formatMessage(messages.mappingModalDropdownMenuTitle)}
								<ChevronDownIcon
									label={formatMessage(messages.mappingModalDropdownChevronDownIconLabel)}
								/>
							</MappingDropdownContainer>
						)}
					</Button>
				)}
				// our data is stored in memory we are never loading
				isLoading={false}
				filter={pickerFilter}
				onSelect={onSelect}
				onLoadMore={onLoadMore}
				onFilterChange={onFilterChange}
				page={page}
				onOpen={onOpen}
				onClose={onClose}
			/>
		</DropdownMenuWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownMenuWrapper = styled.div({
	width: '198px',
	paddingLeft: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingDropdownContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.050', '4px'),
});
